import React from 'react';
import styled from 'styled-components';
import { height } from 'styled-system';
import { Text } from '@dutchie/common/components/rebass-compat';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import GlobalFooter from 'components/global-footer-rebrand';
import MarketingHeader from 'marketing/components/marketing-header';
import Wrapper from 'components/wrapper';
import MetaTags from 'components/meta-tags';

export default function TermsPage() {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down(`sm`));

  return (
    <Wrapper>
      <MetaTags variant='terms' />
      <MarketingHeader variant='legal' />
      <PageContentContainer isMobile={isMobile}>
        <H1>Terms of Service</H1>
        <P>
          <Bold>Last Updated: August 7, 2026</Bold>
        </P>
        <P>
          <Bold>Welcome to Dutchie!</Bold>
        </P>
        <P>
          Courier Plus Inc. (<Bold>“Dutchie</Bold>,<Bold>”</Bold> <Bold>“we</Bold>,<Bold>”</Bold> <Bold>“us</Bold>,
          <Bold>”</Bold> or <Bold>“our”</Bold>) provides its services (described below) to you through its websites,
          including the website located at www.dutchie.com (collectively, the <Bold>“Site”</Bold>), mobile applications
          made available by cannabis dispensaries (<Bold>“Dispensaries”</Bold>) using Dutchie's platform,
          Dispensary-powered eCommerce storefronts, self-service kiosks, payment services, and related technologies and
          services (including through AI-powered features such as conversational shopping guides, voice-based AI
          assistances, and personalized product recommendation engines) (collectively, such services, including any new
          features and applications, and the Site, the <Bold>“Service(s)”</Bold>), subject to the following Terms of
          Service (as amended from time to time, the <Bold>“Terms of Service”</Bold>). We reserve the right, at our sole
          discretion, to change or modify portions of these Terms of Service at any time. If we do this, we will post
          the changes on this page and will indicate at the top of this page the date these terms were last revised. We
          will also notify you, either through the Services user interface, in an email notification or through other
          reasonable means. Any such changes will become effective no earlier than 14 days after they are posted, except
          that changes addressing new functions of the Services or changes made for legal reasons will be effective
          immediately. Your continued use of the Service after the date any such changes become effective constitutes
          your acceptance of the new Terms of Service.
        </P>
        <P>
          <Bold>
            PLEASE READ THESE TERMS OF SERVICE CAREFULLY, AS THEY CONTAIN AN AGREEMENT TO ARBITRATE AND OTHER IMPORTANT
            INFORMATION REGARDING YOUR LEGAL RIGHTS, REMEDIES, AND OBLIGATIONS. THE AGREEMENT TO ARBITRATE REQUIRES
            (WITH LIMITED EXCEPTION) THAT YOU SUBMIT CLAIMS YOU HAVE AGAINST US TO BINDING AND FINAL ARBITRATION, AND
            FURTHER (A) YOU WILL ONLY BE PERMITTED TO PURSUE CLAIMS AGAINST DUTCHIE ON AN INDIVIDUAL BASIS, NOT AS A
            PLAINTIFF OR CLASS MEMBER IN ANY CLASS OR REPRESENTATIVE ACTION OR PROCEEDING, (B) YOU WILL ONLY BE
            PERMITTED TO SEEK RELIEF (INCLUDING MONETARY, INJUNCTIVE, AND DECLARATORY RELIEF) ON AN INDIVIDUAL BASIS,
            AND (C) YOU MAY NOT BE ABLE TO HAVE ANY CLAIMS YOU HAVE AGAINST US RESOLVED BY A JURY OR IN A COURT OF LAW.
          </Bold>
        </P>
        <P>
          In addition, when using certain Services, you may be subject to any additional terms applicable to such
          services that may be posted on the Service from time to time, including, without limitation, the Privacy
          Policy located at <a href='https://dutchie.com/privacy'>https://dutchie.com/privacy</a>. All such terms are
          hereby incorporated by reference into these Terms of Service.
        </P>
        <P>
          <Bold>Key Points</Bold>
        </P>
        <P>
          Dutchie is not a Dispensary. We are a technology platform that enables Dispensaries to serve their customers
          and/or primary caregivers through online reservations, in-store technologies, payment processing, and related
          Services. We do not offer delivery services, set pricing, or maintain any inventory of cannabis or related
          products.{' '}
        </P>
        <P>
          Dutchie is a technology provider and does not itself cultivate, manufacture, sell, deliver, or dispense
          cannabis or cannabis products. Dutchie neither endorses nor is responsible for the accuracy or reliability of
          any information, content, or quality of goods or services offered by the Dispensaries, and cannot make any
          representations or warranties with respect to whether the Dispensaries are compliant with applicable rules and
          regulations. Any transactions you complete through the Services are between you and the applicable Dispensary.
          All questions regarding Dispensaries' products and/or services featured on the Site and/or the Services should
          be directed to the appropriate Dispensaries.
        </P>
        <P>
          The information provided through the Services, including product descriptions, recommendations, availability,
          pricing, and any content generated by automated or artificial intelligence features, is for informational
          purposes only and is not intended to be a substitute for professional medical advice, diagnosis, treatment, or
          prevention of any disease or condition. Dutchie does not recommend or endorse any specific products,
          Dispensaries, tests, procedures, or opinions that may appear on the Site and/or the Services. If you are a
          medical cannabis patient, always seek the advice of a qualified medical professional before making decisions
          based on information available through the Services. Your reliance on any information available through the
          Site and/or the Services is solely at your own risk.
        </P>
        <P>
          <Bold>Disclaimers and Acknowledgements</Bold>
        </P>
        <P>
          <Bold>Dutchie is a Technology Platform:</Bold> You expressly agree and acknowledge that Dutchie: (a) is a
          technology service provider; (b) is not a licensed medical or adult-use cannabis retailer, manufacturer, or
          brand; (c) does not cultivate, manufacture, package, label, test, sell, deliver or dispense medical or
          adult-use cannabis; and (d) does not provide medical advice or product recommendations on its own behalf. Any
          product information, recommendations, or other content made available through the Services, including content
          generated by automated or artificial intelligence features, is derived from information provided by
          Dispensaries or third parties and has not been independently verified by Dutchie. Each Dispensary is solely
          responsible for offering its products and services to you in a legal and compliant manner, including with
          respect to any transactions facilitated through the Services.
        </P>
        <P>
          <Bold>Compliance with Federal Law:</Bold> Cannabis remains a controlled substance under the United States
          Controlled Substances Act, and the legal status of cannabis varies by jurisdiction. Federal, state, and local
          laws governing cannabis may conflict. In Canada, cannabis is regulated under the Cannabis Act (S.C. 2018, c.
          16) and applicable provincial and territorial laws. You acknowledge that it is your sole responsibility to
          understand and comply with all applicable federal, state, provincial, and local laws, rules, and regulations
          governing the purchase, possession, and use of cannabis in your jurisdiction. Dutchie disclaims any and all
          liability relating to your use of the Services in violation of any applicable laws, rules, and/or regulations.
        </P>
        <P>
          <Bold>Compliance with State Law:</Bold> You expressly acknowledge that the laws surrounding recreational and
          medical cannabis use vary by state and locality. You expressly acknowledge and assume full responsibility for
          complying with applicable state and local laws, rules, and regulations where you reside. Dutchie disclaims any
          and all liability relating to the use of its Services in violation of any state laws, rules and/or
          regulations.
        </P>
        <P>
          <Bold>Health Information:</Bold> You expressly acknowledge, agree, and understand that any statements relating
          to cannabis goods, accessories or any other products available on the Site or through the Services have not
          been evaluated by the FDA. Neither the products nor the ingredients in any of the products have been approved
          or endorsed by the FDA or any regulatory agency. The products available on the Site or through the Services
          are not intended to diagnose, treat, cure, mitigate, or prevent any medical condition, illness, or disease.
          The information provided is designed for educational purposes only and is not intended to be a substitute for
          informed medical advice or care. If you are pregnant, nursing, taking medication, or have a medical condition,
          we suggest consulting with a physician before using and products offered or purchased through the Site or
          Services. Without limiting the foregoing, product recommendations, effect-based suggestions, terpene-based
          guidance, and other outputs generated by AI-powered features of the Services are for informational and
          educational purposes only and are not intended to diagnose, treat, cure, mitigate, or prevent any medical
          condition, illness, or disease. You should not rely on AI-generated product information to make health-related
          decisions.
        </P>
        <P>
          <Bold>AI-Powered Features</Bold>
        </P>
        <P>
          <Bold>Generally:</Bold> Certain features of the Services use artificial intelligence and machine learning
          technologies to provide you with product recommendations, answer your questions, and personalize your shopping
          experience (collectively, <Bold>“AI Features”</Bold>). AI Features may include voice-based AI assistants
          accessible by phone, conversational AI shopping guides on Dispensary menus and kiosks, and personalized
          product recommendation engines. When you use an AI Feature, you may be interacting with an AI-powered system
          rather than a human.
        </P>
        <P>
          <Bold>Not Medical Advice:</Bold> Product recommendations, effect-based suggestions, terpene-based guidance,
          and other responses provided by AI Features are for information and educational purposes only. They do not
          constitute medical advice, diagnosis, treatment recommendations, or health guidance from Dutchie or any
          Dispensary. AI Features are not a substitute for the advice of a qualified medical professional. You assume
          all risk associated with your use of products recommended or described by AI Features.
        </P>
        <P>
          <Bold>Accuracy and Limitations:</Bold> AI Features generate responses using machine learning models that may
          produce results that are inaccurate, incomplete, outdated, or inappropriate. Dutchie does not guarantee the
          accuracy, completeness, reliability, or suitability of any AI-generated response. Product availability,
          pricing, and attributes reflected in AI-generated responses are subject to change. You should confirm product
          details with the applicable Dispensary before completing a purchase.
        </P>
        <P>
          <Bold>Personalized Recommendations:</Bold> Certain AI Features may provide personalized product
          recommendations based on your interactions with the Service, including your queries, browsing behavior,
          purchase history, and stated preferences. These recommendations are generated using automated processing and
          are not endorsements by Dutchie. Some recommendations may be influenced by a Dispensary’s product selections,
          featured items, or store-specific configurations.
        </P>
        <P>
          <Bold>Data Collection:</Bold> When you interact with AI Features, Dutchie collects information about your
          interaction on behalf of that Dispensary, including your queries, inputs, and stated preferences, as well as
          inferred product, effect, and terpene preferences. This information is used to provide and improve the AI
          Features and to personalize your experience on behalf of the Dispensary you are interacting with and is
          handled in accordance with the Dispensary’s privacy policy and our Privacy Policy.
        </P>
        <P>
          <Bold>Access and Use of the Service</Bold>
        </P>
        <P>
          <Bold>Eligibility:</Bold> By using the Services, you represent and warrant that you are at least 21 years old
          (or at least 18 years old and a qualified medical cannabis patient in your jurisdiction), are legally entitled
          to enter into these Terms of Service, and have the right, authority, and capacity to abide by these Terms of
          Service. If you do not meet these eligibility requirements, you are prohibited from using, and must not
          attempt to use, the Services.
        </P>
        <P>
          <Bold>Account Registration and Security:</Bold> Certain features of the Services may require or allow you to
          create an account. If you create an account, you agree to provide and maintain true, accurate, current and
          complete information about yourself. You may create an account using third-party authentication services such
          as Google or Apple sign-in. If you do, you authorize us to access and use certain information from those
          services in accordance with our Privacy Policy, and you acknowledge that your use of those services is
          governed by their respective terms and privacy policies. Registration data and certain other information about
          you are governed by our Privacy Policy. You are responsible for maintaining the confidentiality of your login
          credentials and are fully responsible for any and all activities that occur under your account. Dutchie may
          refuse to grant you a username or other identifier that impersonates someone else, is or may be illegal, is or
          may be protected by trademark or other proprietary rights law, is disrespectful, vulgar or otherwise
          offensive, or may cause confusion, as determined by us in our sole discretion. You agree to immediately notify
          Dutchie of any unauthorized use of your account or any other breach of security. Dutchie will not be liable
          for any loss or damage arising from your failure to comply with this Section.
        </P>
        <P>
          <Bold>Modifications to Service:</Bold> Dutchie reserves the right to modify or discontinue, temporarily or
          permanently, the Services (or any part thereof) with or without notice. You agree that Dutchie will not be
          liable to you or to any third party for any modification, suspension or discontinuance of the Services.
        </P>
        <P>
          <Bold>General Practices Regarding Use and Storage: </Bold>You acknowledge that Dutchie may establish general
          practices and limits concerning use of the Services, including the types of account data retained and the
          period of time such data will be stored. Except as required by applicable law or our Privacy Policy, Dutchie
          has no responsibility or liability for the deletion or failure to store any account data, order history, saved
          preferences, or other information associated with your use of the Services. You acknowledge that Dutchie
          reserves the right to terminate accounts that are inactive for an extended period of time. You further
          acknowledge that Dutchie reserves the right to change these general practices and limits at any time, in its
          sole discretion, with or without notice.
        </P>
        <P>
          <Bold>Communications:</Bold> By using the Services, you agree that we may communicate with you regarding
          Dutchie, the Dispensaries, and your use of the Services by SMS, MMS, text message, push notification, or other
          electronic means to your mobile device. Standard wireless carrier message and data rates may apply. You may
          opt out of receiving promotional messages at any time by following the unsubscribe instructions in the
          message. In the event that you change or deactive your mobile telephone number, you agree to promptly update
          your Dutchie account information. By using the Services or creating an account, you consent to receive
          communications from Dutchie electronically, including by email, text message, push notification, or notices
          posted through the Services. You agree that all agreements, notices, disclosures, and other communications
          that Dutchie provides to you electronically satisfy any legal requirement that such communications be in
          writing. To receive electronic communications, you need a device with an internet connection, a current web
          browser, and a valid email address. You may withdraw your consent to receive electronic communications by
          contacting us at support@dutchie.com, but doing so may limit your ability to use certain features of the
          Services.
        </P>
        <P>
          {' '}
          <Bold>Conditions of Use</Bold>
        </P>
        <P>
          <Bold>User Conduct:</Bold> You are solely responsible for your use of the Services and any content or
          information you submit or transmit through the Services. Dutchie reserves the right to investigate and take
          appropriate action against anyone who, in Dutchie’s sole discretion, violates this provision, including
          removing offending content from the Services, suspending or terminating the account of such violators, and
          reporting you to law enforcement authorities. You agree not to use the Services to:
        </P>
        <List>
          <UL>
            <li>
              submit or transmit any content or information that (i) infringes any intellectual property or other
              proprietary rights of any party; (ii) contains software viruses or any other code designed to interrupt,
              destroy, or limit the functionality of any software, hardware, or telecommunications equipment; (iii)
              poses or creates a privacy or security risk to any person; (iv) is unlawful, harmful, threatening,
              abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable; or (v) in the sole judgment
              of Dutchie, restricts or inhibits any other person from using or enjoying the Services, or may expose
              Dutchie or its users to any harm or liability of any type;
            </li>
            <li>
              interfere with or disrupt the Service or servers or networks connected to the Service, or disobey any
              requirements, procedures, policies or regulations of networks connected to the Service;
            </li>
            <li>
              violate any applicable local, state, national or international law, or any regulations having the force of
              law;
            </li>
            <li>
              impersonate any person or entity, or falsely state or otherwise misrepresent your affiliation with a
              person or entity;
            </li>
            <li>solicit personal information from anyone under the age of 18;</li>
            <li>
              misuse AI Features, including by attempting to extract training data, bypass safety filters, generate
              false health or product claims, or manipulate product recommendations;
            </li>
            <li>
              share, sell, or transfer your account credentials, or allow any other person to access the Services
              through your account;
            </li>
            <li>
              further or promote any criminal activity or enterprise or provide instructional information about illegal
              activities; or
            </li>
            <li>
              obtain or attempt to access or otherwise obtain any materials or information through any means not
              intentionally made available or provided for through the Service.
            </li>
          </UL>
        </List>
        <P>
          <Bold>Fees and Payments:</Bold> Dutchie does not charge consumers any fees for use of the Services. All
          payments made through the Services, including payments made via Pay by Bank, are between you and the
          applicable Dispensary. Product pricing, taxes, and any applicable fees (such as delivery fees) are determined
          by the Dispensary, not by Dutchie. Dutchie is not responsible for any pricing errors, overcharges, or billing
          disputes between you and a Dispensary. Any such issues should be directed to the applicable Dispensary.
        </P>
        <P>
          <Bold>Pay by Bank:</Bold> Certain Dispensaries offer Pay by Bank as a payment option through the Services. Pay
          by Bank allows you to make payments directly from your bank account via electronic fund transfer (EFT). When
          you enroll in Pay by Bank, you authorize Dutchie, as agent on behalf of the applicable Dispensary, to initiate
          debit and credit entries to your bank account in accordance with the standing authorization you provide during
          enrollment. Your use of Pay by Bank is subject to the authorization terms presented during enrollment, which
          are incorporated into these Terms of Service by reference. Linking your bank account to Pay by Bank is
          facilitated by Plaid, Inc. and is subject to the Digital Money Management User Terms available at
          business.dutchie.com/legal/digital-money-management-user-terms-plaid. You are responsible for ensuring that
          sufficient funds are available in your bank account for each transaction. If a payment is returned unpaid, you
          may be charged a return fee as described in your authorization. You may revoke your Pay by Bank authorization
          at any time by contacting Dutchie at achoperations@dutchie.com, though revocation will not affect any
          transactions already in process. Dutchie is not responsible for any fees your bank may charge in connection
          with EFT transactions. Pay by Bank transactions are subject to the NACHA Operating Rules and applicable law.
          In the event of an error or unauthorized transaction, please contact Dutchie at achoperations@dutchie.com
          promptly. Dutchie will investigate and resolve errors in accordance with applicable law, including Regulation
          E (12 C.F.R. Part 1005) to the extent applicable.
        </P>
        <P>
          <Bold>Special Notice for International Use; Export Controls:</Bold> The Site and the Services are controlled
          and operated from its facilities in the United States. Dutchie makes no representations that the Site or the
          Services are appropriate or available for use in other locations. Those who access or use the Site or the
          Services from other jurisdictions do so at their own volition and are entirely responsible for compliance with
          all applicable United States and local laws and regulations, including but not limited to export and import
          regulations. You represent and warrant that (i) you are not located in a country that is subject to a U.S.
          Government embargo, or that has been designated by the U.S. Government as a "terrorist supporting" country;
          and (ii) you are not listed on any U.S. Government list of prohibited or restricted parties. Software (defined
          below) available in connection with the Service and the transmission of applicable data, if any, is subject to
          United States export controls. No Software may be downloaded from the Service or otherwise exported or
          re-exported in violation of U.S. export laws. Downloading or using the Software is at your sole risk.
          Recognizing the global nature of the Internet, you agree to comply with all local rules and laws regarding
          your use of the Service, including as it concerns online conduct and acceptable content.
        </P>
        <P>
          <Bold>Commercial Use:</Bold> Unless otherwise expressly authorized herein or in the Service, you agree not to
          display, distribute, license, perform, publish, reproduce, duplicate, copy, create derivative works from,
          modify, sell, resell, exploit, transfer or upload for any commercial purposes, any portion of the Service, use
          of the Service, or access to the Service. The Service is for your personal use.
        </P>
        <P>
          <Bold>Relationship of the Parties:</Bold> Nothing in these Terms of Service creates any agency, partnership,
          joint venture, or employment relationship between Dutchie and any Dispensary, or between Dutchie and you.
          Dutchie is an independent technology provider and does not act as agent for any Dispensary except as expressly
          set forth in the Pay by Bank authorization terms. No Dispensary has authority to bind Dutchie, and Dutchie has
          no authority to bind any Dispensary except as expressly authorized.
        </P>
        <P>
          <Bold>Loyalty Programs:</Bold> Certain Dispensaries may offer loyalty, rewards, or promotional programs
          through the Services. These programs are operated by the applicable Dispensary, not by Dutchie. Loyalty
          points, rewards, and program terms (including expiration, transferability, and redemption) are determined
          solely by the Dispensary. Dutchie provides the technology infrastructure for these programs but is not
          responsible for the accrual, redemption, expiration, or loss of any loyalty points or rewards. Any questions
          or disputes regarding a loyalty program should be directed to the applicable Dispensary.
        </P>
        <P>
          <Bold>Transactions Between Buyers and Sellers</Bold>
        </P>
        <P>
          Dutchie facilitates transactions between you and Dispensaries but is not a party to any transaction you
          complete through the Services. Dutchie does not control and is not liable or responsible for the quality,
          safety, lawfulness, or availability of any products or services offered by Dispensaries, or any Dispensary's
          ability to fulfill an order. You assume all risks associated with any purchase you make through the Services,
          including risks related to product quality, product descriptions, pricing accuracy, delivery, and compliance
          with applicable law.
        </P>
        <P>
          To the extent that you have placed an eCommerce order with a New York-based Dispensary and another individual
          will be picking up the order on your behalf (or on behalf of the individual for whom the order was placed),
          you attest that the individual picking up the order on your behalf is 21 years of age or older.
        </P>
        <P>
          You agree to provide all information and materials as may be reasonably required by Dutchie or a Dispensary in
          connection with your transactions conducted on, through or as a result of use of the Site or Services. Dutchie
          has the right to suspend or terminate your account if you fail to provide the required information and
          materials without liability for any losses or damages arising out of or in connection with such suspension or
          termination.
        </P>
        <P>
          In the event that you have a dispute with any party to a transaction, you agree to release and indemnify
          Dutchie (and our agents, affiliates, directors, officers and employees) from all claims, demands, actions,
          proceedings, costs, expenses and damages (including without limitation any actual, special, incidental or
          consequential damages) arising out of or in connection with such dispute or the transaction.
        </P>
        <P>
          <Bold>Product Descriptions</Bold>
        </P>
        <P>
          Dutchie displays product information as provided by Dispensaries and attempts to present it accurately.
          However, Dutchie is not a Dispensary and does not itself package, test, label, or sell the products available
          on the Site or through the Services. Dutchie does not warrant that product descriptions, images, or other
          content available on the Site or through the Services is accurate, complete, reliable, current, or error-free.
          Product information surfaced through AI-powered features of the Services, including product recommendations,
          terpene profiles, effect descriptions, and similar content, is derived from product catalog data provided by
          Dispensaries and generated by automated systems. Such information is subject to the same limitations described
          in this section and may contain inaccuracies or reflect data that is no longer current.
        </P>
        <P>
          You acknowledge and agree that amounts in product descriptions on the Site and through the Services are
          averages or estimates, and that amounts may vary for individual items, packages, or orders that you receive.
          Amounts on the Site include, without limitation, the level (by weight, mass, volume, or percentage) of THC,
          CBD, other cannabinoids, and terpenes in a product, the total weight, mass, or volume of a product, the size
          or number of individual items in a product, and any other unit of measurement related to a product.
        </P>
        <P>
          {' '}
          <Bold>Third Party Distribution Channels</Bold>
        </P>
        <P>
          Dispensaries may offer mobile applications powered by Dutchie's platform through the Apple App Store, Google
          Play, or other distribution channels (“Distribution Channels”). These applications are published and offered
          by the applicable Dispensary, not by Dutchie. If you download or use a Dispensary's mobile application, you
          may be subject to additional terms of both the Dispensary and the Distribution Channel. To the extent that you
          utilize any other third party products and services in connection with your use of our Services, you agree to
          comply with all applicable terms of any agreement for such third party products and services.
        </P>
        <P>
          To the extent you access the Services through a Dispensary's mobile application on an Apple-branded product,
          you acknowledge and agree that:
        </P>
        <List>
          <UL>
            <li>
              these Terms of Service are between Dutchie and you only, and not with Apple Inc. (“Apple”). The applicable
              Dispensary, not Dutchie or Apple, is responsible for the mobile application and the content thereof;
            </li>
            <li>
              You may not use the Apple-Enabled Software in any manner that is in violation of or inconsistent with the
              Usage Rules set forth for Apple-Enabled Software in, or otherwise be in conflict with, the App Store Terms
              of Service.
            </li>
            <li>
              Your license to use the Apple-Enabled Software is limited to a non-transferable license to use the
              Apple-Enabled Software on an iOS Product that you own or control, as permitted by the Usage Rules set
              forth in the App Store Terms of Service.
            </li>
            <li>
              Apple has no obligation whatsoever to provide any maintenance or support services with respect to the
              Apple-Enabled Software.
            </li>
            <li>
              Apple is not responsible for any product warranties, whether express or implied by law. In the event of
              any failure of the mobile application to conform to any applicable warranty, you may notify Apple, and
              Apple will refund the purchase price for the application to you, if any; and, to the maximum extent
              permitted by applicable law, Apple will have no other warranty obligation whatsoever with respect to the
              mobile application, or any other claims, losses, liabilities, damages, costs or expenses attributable to
              any failure to conform to any warranty, which will be the applicable Dispensary's sole responsibility, to
              the extent it cannot be disclaimed under applicable law.
            </li>
            <li>
              The applicable Dispensary, not Apple, is responsible for addressing any claims of you or any third party
              relating to the mobile application or your possession and/or use of that application, including, but not
              limited to: (i) product liability claims; (ii) any claim that the application fails to conform to any
              applicable legal or regulatory requirement; and (iii) claims arising under consumer protection or similar
              legislation.
            </li>
            <li>
              In the event of any third party claim that the mobile application or the end-user’s possession and use of
              that application infringes that third party’s intellectual property rights, as between the applicable
              Dispensary and Apple, the Dispensary, not Apple, will be solely responsible for the investigation,
              defense, settlement and discharge of any such intellectual property infringement claim;
            </li>
            <li>
              You represent and warrant that (i) you are not located in a country that is subject to a U.S. Government
              embargo, or that has been designated by the U.S. Government as a “terrorist supporting” country; and (ii)
              you are not listed on any U.S. Government list of prohibited or restricted parties.
            </li>
            <li>
              if you have any questions, complaints or claims with respect to a Dispensary's mobile application, they
              should be directed to the applicable Dispensary.
            </li>
          </UL>
        </List>
        <P>
          You acknowledge and agree that Apple, and Apple’s subsidiaries, are third party beneficiaries of these Terms
          of Service with respect to your use of any Dispensary mobile application on an Apple-branded product, and
          that, upon your acceptance of the terms and conditions of these Terms of Service, Apple will have the right
          (and will be deemed to have accepted the right) to enforce these Terms of Service against you as a third party
          beneficiary thereof.
        </P>
        <P>
          <Bold>Intellectual Property Rights</Bold>
        </P>
        <P>
          <Bold>Service Content, Software and Trademarks:</Bold> You acknowledge and agree that the Service may contain
          content or features (“Service Content”) that are protected by copyright, patent, trademark, trade secret or
          other proprietary rights and laws. Except as expressly authorized by Dutchie, you agree not to modify, copy,
          frame, scrape, rent, lease, loan, sell, distribute or create derivative works based on the Service or the
          Service Content, in whole or in part, except that the foregoing does not apply to your own User Content (as
          defined below) that you legally upload to the Service. In connection with your use of the Service you will not
          engage in or use any data mining, robots, scraping or similar data gathering or extraction methods. If you are
          blocked by Dutchie from accessing the Service (including by blocking your IP address), you agree not to
          implement any measures to circumvent such blocking (e.g., by masking your IP address or using a proxy IP
          address). Any use of the Service or the Service Content other than as specifically authorized herein is
          strictly prohibited. The technology and software underlying the Service, including Dutchie's platform, APIs,
          and related infrastructure (the "Software"), are the property of Dutchie, our affiliates and our partners. You
          agree not to copy, modify, create a derivative work of, reverse engineer, reverse assemble or otherwise
          attempt to discover any source code, sell, assign, sublicense, or otherwise transfer any right in the
          Software. Any rights not expressly granted herein are reserved by Dutchie.
        </P>
        <P>
          The Dutchie name and logos are trademarks and service marks of Dutchie (collectively the “Dutchie
          Trademarks”). Other Dutchie, product, and service names and logos used and displayed via the Service may be
          trademarks or service marks of their respective owners who may or may not endorse or be affiliated with or
          connected to Dutchie. Nothing in this Terms of Service or the Service should be construed as granting, by
          implication, estoppel, or otherwise, any license or right to use any of Dutchie Trademarks displayed on the
          Service, without our prior written permission in each instance. All goodwill generated from the use of Dutchie
          Trademarks will inure to our exclusive benefit.
        </P>
        <P>
          <Bold>Third Party Material:</Bold> Under no circumstances will Dutchie be liable in any way for any content or
          materials of any third parties (including users), including, but not limited to, for any errors or omissions
          in any content, or for any loss or damage of any kind incurred as a result of the use of any such content. You
          acknowledge that Dutchie does not pre-screen content, but that Dutchie and its designees will have the right
          (but not the obligation) in their sole discretion to refuse or remove any content that is available via the
          Service. Without limiting the foregoing, Dutchie and its designees will have the right to remove any content
          that violates these Terms of Service or is deemed by Dutchie, in its sole discretion, to be otherwise
          objectionable. You agree that you must evaluate, and bear all risks associated with, the use of any content,
          including any reliance on the accuracy, completeness, or usefulness of such content.
        </P>
        <P>
          <Bold>User Content Transmitted Through the Service:</Bold> With respect to any content, information, or
          materials you submit through the Service, including product reviews, AI Feature queries, saved preferences,
          and account information (collectively, “User Content”), you represent and warrant that you own all right,
          title and interest in and to such User Content, including, without limitation, all intellectual property
          rights contained therein. By submitting any User Content you hereby grant and will grant Dutchie and its
          affiliated companies a nonexclusive, worldwide, royalty free, fully paid up, transferable, sublicensable,
          perpetual, irrevocable license to copy, display, perform, distribute, store, modify and otherwise use your
          User Content in connection with the operation, improvement, and personalization of the Service.
        </P>
        <P>
          You acknowledge and agree that any questions, comments, suggestions, ideas, feedback or other information
          about the Service (“Submissions”), provided by you to Dutchie are non-confidential and Dutchie will be
          entitled to the unrestricted use and dissemination of these Submissions for any purpose, commercial or
          otherwise, without acknowledgment or compensation to you.
        </P>
        <P>
          You acknowledge and agree that Dutchie may preserve content and may also disclose content if required to do so
          by law or in the good faith belief that such preservation or disclosure is reasonably necessary to: (a) comply
          with legal process, applicable laws or government requests; (b) enforce these Terms of Service; (c) respond to
          claims that any content violates the rights of third parties; or (d) protect the rights, property, or personal
          safety of Dutchie, its users and the public. You understand that the technical processing and transmission of
          the Service, including your content, may involve (a) transmissions over various networks; and (b) changes to
          conform and adapt to technical requirements of connecting networks or devices.
        </P>
        <P>
          <Bold>Copyright Complaints:</Bold> Dutchie respects the intellectual property of others, and we ask our users
          to do the same. If you believe that your work has been copied in a way that constitutes copyright
          infringement, or that your intellectual property rights have been otherwise violated, you should notify
          Dutchie of your infringement claim in accordance with the procedure set forth below.
        </P>
        <P>
          Dutchie will process and investigate notices of alleged infringement and will take appropriate actions under
          the Digital Millennium Copyright Act (“DMCA”) and other applicable intellectual property laws with respect to
          any alleged or actual infringement. A notification of claimed copyright infringement should be emailed to
          Dutchie’s Copyright Agent at <a href='mailto:support@dutchie.com'>support@dutchie.com</a> (Subject line: “DMCA
          Takedown Request”). You may also contact us by mail at:
        </P>
        <P>68 Harrison Avenue, Suite 605, PMB 49488, Boston, Massachusetts 02111</P>
        <P>To be effective, the notification must be in writing and contain the following information:</P>
        <List>
          <UL>
            <li>
              an electronic or physical signature of the person authorized to act on behalf of the owner of the
              copyright or other intellectual property interest;
            </li>
            <li>
              a description of the copyrighted work or other intellectual property that you claim has been infringed;
            </li>
            <li>
              a description of where the material that you claim is infringing is located on the Service, with enough
              detail that we may find it on the Service;
            </li>
            <li>your address, telephone number, and email address;</li>
            <li>
              a statement by you that you have a good faith belief that the disputed use is not authorized by the
              copyright or intellectual property owner, its agent, or the law;
            </li>
            <li>
              a statement by you, made under penalty of perjury, that the above information in your Notice is accurate
              and that you are the copyright or intellectual property owner or authorized to act on the copyright or
              intellectual property owner’s behalf.
            </li>
          </UL>
        </List>
        <P>
          <Bold>Counter-Notice:</Bold> If you believe that your User Content that was removed (or to which access was
          disabled) is not infringing, or that you have the authorization from the copyright owner, the copyright
          owner’s agent, or pursuant to the law, to upload and use the content in your User Content, you may send a
          written counter-notice containing the following information to the Copyright Agent:
        </P>
        <List>
          <UL>
            <li>your physical or electronic signature;</li>
            <li>
              identification of the content that has been removed or to which access has been disabled and the location
              at which the content appeared before it was removed or disabled;
            </li>
            <li>
              a statement that you have a good faith belief that the content was removed or disabled as a result of
              mistake or a misidentification of the content; and
            </li>
            <li>
              your name, address, telephone number, and email address, a statement that you consent to the jurisdiction
              of the federal court located within the Commonwealth of Massachusetts and a statement that you will accept
              service of process from the person who provided notification of the alleged infringement.
            </li>
          </UL>
        </List>
        <P>
          If a counter-notice is received by the Copyright Agent, Dutchie will send a copy of the counter-notice to the
          original complaining party informing that person that it may replace the removed content or cease disabling it
          in 10 business days. Unless the copyright owner files an action seeking a court order against the content
          provider, member or user, the removed content may be replaced, or access to it restored, in 10 to 14 business
          days or more after receipt of the counter-notice, at our sole discretion.
        </P>
        <P>
          <Bold>Repeat Infringer Policy:</Bold> In accordance with the DMCA and other applicable law, Dutchie has
          adopted a policy of terminating, in appropriate circumstances and at Dutchie's sole discretion, users who are
          deemed to be repeat infringers. Dutchie may also at its sole discretion limit access to the Service and/or
          terminate the memberships of any users who infringe any intellectual property rights of others, whether or not
          there is any repeat infringement.{' '}
        </P>
        <P>
          <Bold>Third Party Websites</Bold>
        </P>
        <P>
          The Service may provide, or third parties may provide, links or other access to other sites and resources on
          the Internet. Dutchie has no control over such sites and resources and Dutchie is not responsible for and does
          not endorse such sites and resources. You further acknowledge and agree that Dutchie will not be responsible
          or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection
          with use of or reliance on any content, events, goods or services available on or through any such site or
          resource. Any dealings you have with third parties found while using the Service are between you and the third
          party, and you agree that Dutchie is not liable for any loss or claim that you may have against any such third
          party.
        </P>
        <P>
          {' '}
          <Bold>Indemnity and Release</Bold>
        </P>
        <P>
          You agree to release, indemnify and hold Dutchie and its affiliates and their officers, employees, directors
          and agents (collectively, “Indemnitees”) harmless from any and all losses, damages, expenses, including
          reasonable attorneys’ fees, rights, claims, actions of any kind and injury (including death) arising out of or
          relating to your use of the Service, any User Content, your connection to the Service, your violation of these
          Terms of Service or your violation of any rights of another. If you are a California resident, you waive
          California Civil Code Section 1542, which says: “A general release does not extend to claims which the
          creditor does not know or suspect to exist in his favor at the time of executing the release, which if known
          by him must have materially affected his settlement with the debtor.” If you are a resident of another
          jurisdiction, you waive any comparable statute or doctrine.
        </P>
        <P>
          {' '}
          <Bold>Disclaimer of Warranties</Bold>
        </P>
        <P>
          YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK. THE SERVICE IS PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS.
          DUTCHIE EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED OR STATUTORY, INCLUDING, BUT
          NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE AND
          NON-INFRINGEMENT.
        </P>
        <P>
          DUTCHIE MAKES NO WARRANTY THAT (I) THE SERVICE WILL MEET YOUR REQUIREMENTS, (II) THE SERVICE WILL BE
          UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE, (III) THE RESULTS THAT MAY BE OBTAINED FROM THE USE OF THE
          SERVICE WILL BE ACCURATE OR RELIABLE, OR (IV) THE QUALITY OF ANY PRODUCTS, SERVICES, INFORMATION, OR OTHER
          MATERIAL PURCHASED OR OBTAINED BY YOU THROUGH THE SERVICE WILL MEET YOUR EXPECTATIONS.
        </P>
        <P>
          DUTCHIE MAKES NO REPRESENTATION, WARRANTY, OR GUARANTY AS TO THE RELIABILITY, SAFETY, TIMELINESS, QUALITY,
          SUITABILITY OR AVAILABILITY OF ANY SERVICES, PRODUCTS OR GOODS OBTAINED BY THIRD PARTIES THROUGH THE USE OF
          THE SERVICES. YOU ACKNOWLEDGE AND AGREE THAT THE ENTIRE RISK ARISING OUT OF YOUR USE OF THE SERVICES, AND ANY
          THIRD-PARTY SERVICES OR PRODUCTS, REMAINS SOLELY WITH YOU, TO THE MAXIMUM EXTENT PERMITTED BY LAW.
        </P>
        <P>
          <Bold>Limitation of Liability</Bold>
        </P>
        <P>
          YOU EXPRESSLY UNDERSTAND AND AGREE THAT DUTCHIE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
          CONSEQUENTIAL, EXEMPLARY DAMAGES, OR DAMAGES FOR LOSS OF PROFITS INCLUDING BUT NOT LIMITED TO, DAMAGES FOR
          LOSS OF GOODWILL, USE, DATA OR OTHER INTANGIBLE LOSSES (EVEN IF DUTCHIE HAS BEEN ADVISED OF THE POSSIBILITY OF
          SUCH DAMAGES), WHETHER BASED ON CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY OR OTHERWISE, RESULTING FROM: (I)
          THE USE OR THE INABILITY TO USE THE SERVICE; (II) THE COST OF PROCUREMENT OF SUBSTITUTE GOODS AND SERVICES
          RESULTING FROM ANY GOODS, DATA, INFORMATION OR SERVICES PURCHASED OR OBTAINED OR MESSAGES RECEIVED OR
          TRANSACTIONS ENTERED INTO THROUGH OR FROM THE SERVICE; (III) UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR
          TRANSMISSIONS OR DATA; (IV) STATEMENTS OR CONDUCT OF ANY THIRD PARTY ON THE SERVICE; OR (V) ANY OTHER MATTER
          RELATING TO THE SERVICE. IN NO EVENT WILL DUTCHIE’S TOTAL LIABILITY TO YOU FOR ALL DAMAGES, LOSSES OR CAUSES
          OF ACTION EXCEED THE AMOUNT YOU HAVE PAID DUTCHIE IN THE LAST SIX (6) MONTHS, OR, IF GREATER, ONE HUNDRED
          DOLLARS ($100).
        </P>
        <P>
          SOME JURISDICTIONS DO NOT ALLOW THE DISCLAIMER OR EXCLUSION OF CERTAIN WARRANTIES OR THE LIMITATION OR
          EXCLUSION OF LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES. ACCORDINGLY, SOME OF THE ABOVE LIMITATIONS SET
          FORTH ABOVE MAY NOT APPLY TO YOU OR BE ENFORCEABLE WITH RESPECT TO YOU. IF YOU ARE DISSATISFIED WITH ANY
          PORTION OF THE SERVICE OR WITH THESE TERMS OF SERVICE, YOUR SOLE AND EXCLUSIVE REMEDY IS TO DISCONTINUE USE OF
          THE SERVICE.
        </P>
        <P>
          IF YOU ARE A USER FROM ANY JURISDICTION THAT LIMITS THE SCOPE OF WARRANTY DISCLAIMERS OR LIABILITY
          LIMITATIONS, THE FOREGOING SECTIONS TITLED “DISCLAIMER OF WARRANTIES” AND “LIMITATION OF LIABILITY” ARE
          INTENDED TO BE ONLY AS BROAD AS IS PERMITTED UNDER THE LAWS OF YOUR JURISDICTION. IF ANY PORTION OF THESE
          SECTIONS IS HELD TO BE INVALID UNDER THE LAWS OF YOUR JURISDICTION, THE INVALIDITY OF SUCH PORTION SHALL NOT
          AFFECT THE VALIDITY OF THE REMAINING PORTIONS OF THE APPLICABLE SECTIONS.
        </P>
        <P>
          <Bold>
            Dispute Resolution By Binding Arbitration: PLEASE READ THIS SECTION CAREFULLY AS IT AFFECTS YOUR RIGHTS.
          </Bold>
        </P>
        <P>
          <Bold>Agreement to Arbitrate</Bold>
        </P>
        <P>
          This Dispute Resolution by Binding Arbitration section is referred to in this Terms of Service as the
          “Arbitration Agreement.” You agree that any and all disputes or claims that have arisen or may arise between
          you and Dutchie, whether arising out of or relating to this Terms of Service (including any alleged breach
          thereof), the Services, any advertising, any aspect of the relationship or transactions between us, shall be
          resolved exclusively through final and binding arbitration, rather than a court, in accordance with the terms
          of this Arbitration Agreement, except that you may assert individual claims in small claims court, if your
          claims qualify. Further, this Arbitration Agreement does not preclude you from bringing issues to the
          attention of federal, state, or local agencies, and such agencies can, if the law allows, seek relief against
          us on your behalf. You agree that, by entering into this Terms of Service, you and Dutchie are each waiving
          the right to a trial by jury or to participate in a class action. Your rights will be determined by a neutral
          arbitrator, not a judge or jury. The Federal Arbitration Act governs the interpretation and enforcement of
          this Arbitration Agreement.
        </P>
        <P>
          <Bold>Prohibition of Class and Representative Actions and Non-Individualized Relief</Bold>
        </P>
        <P>
          <Bold>
            YOU AND DUTCHIE AGREE THAT EACH OF US MAY BRING CLAIMS AGAINST THE OTHER ONLY ON AN INDIVIDUAL BASIS AND NOT
            AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE ACTION OR PROCEEDING. UNLESS BOTH
            YOU AND DUTCHIE AGREE OTHERWISE, THE ARBITRATOR MAY NOT CONSOLIDATE OR JOIN MORE THAN ONE PERSON’S OR
            PARTY’S CLAIMS AND MAY NOT OTHERWISE PRESIDE OVER ANY FORM OF A CONSOLIDATED, REPRESENTATIVE, OR CLASS
            PROCEEDING. ALSO, THE ARBITRATOR MAY AWARD RELIEF (INCLUDING MONETARY, INJUNCTIVE, AND DECLARATORY RELIEF)
            ONLY IN FAVOR OF THE INDIVIDUAL PARTY SEEKING RELIEF AND ONLY TO THE EXTENT NECESSARY TO PROVIDE RELIEF
            NECESSITATED BY THAT PARTY’S INDIVIDUAL CLAIM(S), EXCEPT THAT YOU MAY PURSUE A CLAIM FOR AND THE ARBITRATOR
            MAY AWARD PUBLIC INJUNCTIVE RELIEF UNDER APPLICABLE LAW TO THE EXTENT REQUIRED FOR THE ENFORCEABILITY OF
            THIS PROVISION.
          </Bold>
        </P>
        <P>
          <Bold>Pre-Arbitration Dispute Resolution</Bold>
        </P>
        <P>
          Dutchie is always interested in resolving disputes amicably and efficiently, and most customer concerns can be
          resolved quickly and to the customer’s satisfaction by emailing customer support at{' '}
          <a href='mailto:support@dutchie.com'>support@dutchie.com</a>. If such efforts prove unsuccessful, a party who
          intends to seek arbitration must first send to the other, by certified mail, a written Notice of Dispute
          (“Notice”). The Notice to Dutchie should be sent to 68 Harrison Avenue, Suite 605, PMB 49488, Boston,
          Massachusetts 02111 (“Notice Address”), with a mandatory copy sent via email to legal@dutchie.com. The Notice
          must (i) describe the nature and basis of the claim or dispute and (ii) set forth the specific relief sought.
          If Dutchie and you do not resolve the claim within sixty (60) calendar days after the Notice is received, you
          or Dutchie may commence an arbitration proceeding. During the arbitration, the amount of any settlement offer
          made by Dutchie or you shall not be disclosed to the arbitrator until after the arbitrator determines the
          amount, if any, to which you or Dutchie is entitled.
        </P>
        <P>
          <Bold>Arbitration Procedures</Bold>
        </P>
        <P>
          Arbitration will be conducted by a neutral arbitrator in accordance with the American Arbitration
          Association’s (“AAA”) rules and procedures, including the AAA’s Consumer Arbitration Rules (collectively, the
          “AAA Rules”), as modified by this Arbitration Agreement. For information on the AAA, please visit its website,{' '}
          <a href='http://www.adr.org/'>http://www.adr.org</a>. Information about the AAA Rules and fees for consumer
          disputes can be found at the AAA’s consumer arbitration page, http://www.adr.org/consumer_arbitration. If
          there is any inconsistency between any term of the AAA Rules and any term of this Arbitration Agreement, the
          applicable terms of this Arbitration Agreement will control unless the arbitrator determines that the
          application of the inconsistent Arbitration Agreement terms would not result in a fundamentally fair
          arbitration. The arbitrator must also follow the provisions of these Terms of Service as a court would. All
          issues are for the arbitrator to decide, including, but not limited to, issues relating to the scope,
          enforceability, and arbitrability of this Arbitration Agreement. Although arbitration proceedings are usually
          simpler and more streamlined than trials and other judicial proceedings, the arbitrator can award the same
          damages and relief on an individual basis that a court can award to an individual under the Terms of Service
          and applicable law. Decisions by the arbitrator are enforceable in court and may be overturned by a court only
          for very limited reasons.
        </P>
        <P>
          Unless Dutchie and you agree otherwise, any arbitration hearings will take place in a reasonably convenient
          location for both parties with due consideration of their ability to travel and other pertinent circumstances.
          If the parties are unable to agree on a location, the determination shall be made by AAA. If your claim is for
          $10,000 or less, Dutchie agrees that you may choose whether the arbitration will be conducted solely on the
          basis of documents submitted to the arbitrator, through a telephonic or video hearing, or by an in-person
          hearing as established by the AAA Rules. If your claim exceeds $10,000, the right to a hearing will be
          determined by the AAA Rules. Regardless of the manner in which the arbitration is conducted, the arbitrator
          shall issue a reasoned written decision sufficient to explain the essential findings and conclusions on which
          the award is based.
        </P>
        <P>
          <Bold>Costs of Arbitration</Bold>
        </P>
        <P>
          Payment of all filing, administration, and arbitrator fees (collectively, the “Arbitration Fees”) will be
          governed by the AAA Rules, unless otherwise provided in this Arbitration Agreement. If the value of the relief
          sought is $75,000 or less, at your request, Dutchie will pay all Arbitration Fees. If the value of relief
          sought is more than $75,000 and you are able to demonstrate to the arbitrator that you are economically unable
          to pay your portion of the Arbitration Fees or if the arbitrator otherwise determines for any reason that you
          should not be required to pay your portion of the Arbitration Fees, Dutchie will pay your portion of such
          fees. In addition, if you demonstrate to the arbitrator that the costs of arbitration will be prohibitive as
          compared to the costs of litigation, Dutchie will pay as much of the Arbitration Fees as the arbitrator deems
          necessary to prevent the arbitration from being cost-prohibitive. Any payment of attorneys’ fees will be
          governed by the AAA Rules.
        </P>
        <P>
          <Bold>Confidentiality</Bold>
        </P>
        <P>
          All aspects of the arbitration proceeding, and any ruling, decision, or award by the arbitrator, will be
          strictly confidential for the benefit of all parties.
        </P>
        <P>
          <Bold>Severability</Bold>
        </P>
        <P>
          If a court or the arbitrator decides that any term or provision of this Arbitration Agreement (other than the
          subsection (b) titled “Prohibition of Class and Representative Actions and Non-Individualized Relief” above)
          is invalid or unenforceable, the parties agree to replace such term or provision with a term or provision that
          is valid and enforceable and that comes closest to expressing the intention of the invalid or unenforceable
          term or provision, and this Arbitration Agreement shall be enforceable as so modified. If a court or the
          arbitrator decides that any of the provisions of subsection (b) above titled “Prohibition of Class and
          Representative Actions and Non-Individualized Relief” are invalid or unenforceable, then the entirety of this
          Arbitration Agreement shall be null and void, unless such provisions are deemed to be invalid or unenforceable
          solely with respect to claims for public injunctive relief. The remainder of the Terms of Service will
          continue to apply.
        </P>
        <P>
          <Bold>Future Changes to Arbitration Agreement</Bold>
        </P>
        <P>
          Notwithstanding any provision in this Terms of Service to the contrary, Dutchie agrees that if it makes any
          future change to this Arbitration Agreement (other than a change to the Notice Address) while you are a user
          of the Services, you may reject any such change by sending Dutchie written notice within thirty (30) calendar
          days of the change to the Notice Address provided above. By rejecting any future change, you are agreeing that
          you will arbitrate any dispute between us in accordance with the language of this Arbitration Agreement as of
          the date you first accepted these Terms of Service (or accepted any subsequent changes to these Terms of
          Service).
        </P>
        <P>
          {' '}
          <Bold>Termination</Bold>
        </P>
        <P>
          You agree that Dutchie, in its sole discretion, may suspend or terminate your account (or any part thereof) or
          use of the Service and remove and discard any content within the Service, for any reason, including, without
          limitation, for lack of use or if Dutchie believes that you have violated or acted inconsistently with the
          letter or spirit of these Terms of Service. Any suspected fraudulent, abusive or illegal activity that may be
          grounds for termination of your use of Service, may be referred to appropriate law enforcement authorities.
          Dutchie may also in its sole discretion and at any time discontinue providing the Service, or any part
          thereof, with or without notice. You agree that any termination of your access to the Service under any
          provision of this Terms of Service may be effected without prior notice, and acknowledge and agree that
          Dutchie may immediately deactivate or delete your account and all related information and files in your
          account and/or bar any further access to such files or the Service. Further, you agree that Dutchie will not
          be liable to you or any third party for any termination of your access to the Service.
        </P>
        <P>
          <Bold>Force Majeure:</Bold> Dutchie will not be liable for any delay or failure to perform any obligation
          under these Terms of Service where the delay or failure results from any cause beyond Dutchie's reasonable
          control, including acts of God, labor disputes, internet or telecommunications failures, shortages of or
          inability to obtain materials, government orders, or actions of third-party service providers.
        </P>
        <P>
          {' '}
          <Bold>User Disputes</Bold>
        </P>
        <P>
          You agree that you are solely responsible for your interactions with any other user in connection with the
          Service and Dutchie will have no liability or responsibility with respect thereto. Dutchie reserves the right,
          but has no obligation, to become involved in any way with disputes between you and any other user of the
          Service.
        </P>
        <P>
          {' '}
          <Bold>General</Bold>
        </P>
        <P>
          These Terms of Service, together with any additional terms you agree to when using particular features of the
          Services (such as Pay by Bank terms or Dispensary-specific terms), constitute the entire agreement between you
          and Dutchie and govern your use of the Service, superseding any prior agreements between you and Dutchie with
          respect to the Service. You also may be subject to additional terms and conditions that may apply when you use
          affiliate or third party services, third party content or third party software. These Terms of Service will be
          governed by the laws of the Commonwealth of Massachusetts without regard to its conflict of law provisions.
          With respect to any disputes or claims not subject to arbitration, as set forth above, you and Dutchie agree
          to submit to the personal and exclusive jurisdiction of the state and federal courts located within Suffolk
          County, Massachusetts. The failure of Dutchie to exercise or enforce any right or provision of these Terms of
          Service will not constitute a waiver of such right or provision. If any provision of these Terms of Service is
          found by a court of competent jurisdiction to be invalid, the parties nevertheless agree that the court should
          endeavor to give effect to the parties’ intentions as reflected in the provision, and the other provisions of
          these Terms of Service remain in full force and effect. You agree that regardless of any statute or law to the
          contrary, any claim or cause of action arising out of or related to use of the Service or these Terms of
          Service must be filed within 1 year after such claim or cause of action arose or be forever barred. A printed
          version of this agreement and of any notice given in electronic form will be admissible in judicial or
          administrative proceedings based upon or relating to this agreement to the same extent and subject to the same
          conditions as other business documents and records originally generated and maintained in printed form. You
          may not assign this Terms of Service without the prior written consent of Dutchie, but Dutchie may assign or
          transfer this Terms of Service, in whole or in part, without restriction. The section titles in these Terms of
          Service are for convenience only and have no legal or contractual effect. Notices to you may be made via
          either email or regular mail. The Service may also provide notices to you of changes to these Terms of Service
          or other matters by displaying notices or links to notices generally on the Service.
        </P>
        <P>
          {' '}
          <Bold>Your Privacy</Bold>
        </P>
        <P>
          At Dutchie, we respect the privacy of our users. For details please see our Privacy Policy. By using the
          Service, you consent to our collection and use of personal data as outlined therein.
        </P>
        <P>
          <Bold>Accessibility:</Bold> Dutchie is committed to making the Services accessible. For information about the
          accessibility of the Services, please visit dutchie.com/accessibility. If you experience any difficulty
          accessing or using the Services, please contact us at support@dutchie.com.
        </P>
        <P>
          {' '}
          <Bold>Notice for California Users</Bold>
        </P>
        <P>
          Under California Civil Code Section 1789.3, users of the Service from California are entitled to the following
          specific consumer rights notice: The Complaint Assistance Unit of the Division of Consumer Services of the
          California Department of Consumer Affairs may be contacted in writing at 1625 North Market Blvd., Suite N 112,
          Sacramento, CA 95834, or by telephone at (916) 445-1254 or (800) 952-5210. You may contact us at Courier Plus,
          Inc. d/b/a Dutchie, 68 Harrison Avenue, Suite 605, PMB 49488, Boston, Massachusetts 02111, or by telephone at
          (866) 838-8244.
        </P>
        <P>
          <Bold>Questions? Concerns? Suggestions?</Bold>
        </P>
        <P>
          Please contact us at <a href='mailto:support@dutchie.com'>support@dutchie.com</a> to report any violations of
          these Terms of Service or to pose any questions regarding this Terms of Service or the Service.
        </P>
      </PageContentContainer>
      <GlobalFooter />
    </Wrapper>
  );
}

export const PageContentContainer = styled.div`
  padding: ${({ isMobile }) => (isMobile ? `127px 25px 110px 25px` : `110px 245px 170px 245px`)};

  ${height}
`;

export const Bold = styled.span`
  font-weight: bold;
  /* Disable the Matter font's contextual alternates so legal list markers like
     "(c)", "(r)", and "(tm)" render literally instead of ligating into ©, ®, ™. */
  font-feature-settings: 'calt' 0;
  i {
    font-style: italic;
  }
`;

export const H1 = styled(Text)`
  font-size: 26.87px;
  font-weight: bold;
  line-height: 1;
  margin-bottom: 20px;
  margin-top: 0;
`;

export const H2 = styled.h2`
  font-size: 1rem;
  font-weight: bolder;
  line-height: 1.5;
`;

export const SectionHeader = styled.h3`
  font-size: 1rem;
  font-weight: bolder;
  line-height: 1.5;
  margin-bottom: 20px;

  strong {
    text-decoration: none;
  }
`;

export const P = styled.p`
  font-size: 13px;
  line-height: 22px;
  margin-bottom: 20px;
  padding-right: 10px;
  /* Disable the Matter font's contextual alternates so legal list markers like
     "(c)", "(r)", and "(tm)" render literally instead of ligating into ©, ®, ™. */
  font-feature-settings: 'calt' 0;

  > a {
    color: ${({ theme }) => theme.colors.sapphire};
    font-weight: 700;
  }
`;

export const List = styled.div`
  font-size: 13px;
  line-height: 22px;
  margin-bottom: 20px;
  padding-right: 10px;
  /* Disable the Matter font's contextual alternates so legal list markers like
     "(c)", "(r)", and "(tm)" render literally instead of ligating into ©, ®, ™. */
  font-feature-settings: 'calt' 0;
`;

export const OL = styled.ol`
  counter-reset: list;
  margin: 15px 25px;
  li {
    list-style: none;
  }
  li:before {
    content: counter(list, lower-alpha) '. ';
    counter-increment: list;
    font-weight: bold;
  }
`;

export const UL = styled.ul`
  list-style: initial;
  margin: 15px 25px;
`;
